import React from 'react';
import { ChannelCallback, ChannelResponseEnum, IArcSocketContext } from './types';
import { ACCESS_TOKEN_KEY, BASE_SOCKET_URL, RECONNECT_TIME } from '@src/core/config';
import { trackError } from '@src/core/sentry';

export const ArcSocketContext = React.createContext<IArcSocketContext>({
  readyState: false,
  arcSocket: () => null,
  subscribe: () => {},
  unsubscribe: () => {}
});

const ArcSocketProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const socket = React.useRef<WebSocket>();
  const arcChannels = React.useRef<Record<string, ChannelCallback>>({});
  const reconnectTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const isUnmountedRef = React.useRef(false);
  const [readyState, setReadyState] = React.useState<boolean>(false);

  const arcSocket = React.useCallback(() => socket.current ?? null, []);

  const subscribe = React.useCallback((channel: ChannelResponseEnum, callback: ChannelCallback) => {
    arcChannels.current[channel] = callback;
  }, []);

  const unsubscribe = React.useCallback((channel: ChannelResponseEnum) => {
    delete arcChannels.current[channel];
  }, []);

  const clearReconnectTimer = React.useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
  }, []);

  const cleanupSocket = React.useCallback((ws: WebSocket | undefined | null) => {
    if (!ws) return;
    ws.onopen = null;
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    try {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        ws.close();
      }
    } catch {
      // noop
    }
  }, []);

  const createSocket = React.useCallback(() => {
    if (isUnmountedRef.current) return;

    const prev = socket.current;
    if (prev) cleanupSocket(prev);

    const ws = new WebSocket(BASE_SOCKET_URL);
    socket.current = ws;

    ws.onopen = () => {
      if (isUnmountedRef.current) {
        cleanupSocket(ws);
        return;
      }
      setReadyState(true);
      const token = localStorage.getItem(ACCESS_TOKEN_KEY);
      ws.send(JSON.stringify({ type: 'AUTH', token }));
    };

    ws.onclose = () => {
      setReadyState(false);
      if (isUnmountedRef.current) return;
      clearReconnectTimer();
      reconnectTimer.current = setTimeout(() => {
        reconnectTimer.current = null;
        createSocket();
      }, RECONNECT_TIME);
    };

    ws.onmessage = (event) => {
      try {
        const { type, ...data } = JSON.parse(event.data);
        const handler = arcChannels.current[type];
        if (handler) handler(data);
      } catch (error: unknown) {
        trackError(error instanceof Error ? error : new Error(String(error)), {
          code: 'FE_0015',
          extra: {
            rawLength: typeof event.data === 'string' ? event.data.length : undefined,
          },
        });
      }
    };
  }, [cleanupSocket, clearReconnectTimer]);

  React.useEffect(() => {
    isUnmountedRef.current = false;
    createSocket();

    return () => {
      isUnmountedRef.current = true;
      clearReconnectTimer();
      cleanupSocket(socket.current);
      socket.current = undefined;
      arcChannels.current = {};
    };
  }, [createSocket, cleanupSocket, clearReconnectTimer]);

  const value = React.useMemo<IArcSocketContext>(() => ({
    arcSocket,
    subscribe,
    unsubscribe,
    readyState,
  }), [arcSocket, subscribe, unsubscribe, readyState]);

  return (
    <ArcSocketContext.Provider value={value}>
      {children}
    </ArcSocketContext.Provider>
  );

};

export default ArcSocketProvider;
